import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, LineChart, Workflow, Loader2, Eye, EyeOff } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth, homeFor } from "../context/AuthContext";
import { errMsg } from "../lib/api";
import { lastEmail, rememberEmail } from "../lib/session";
import { Checkbox } from "../components/ui/checkbox";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState(new URLSearchParams(window.location.search).get("email") || lastEmail);
  const [saveEmail, setSaveEmail] = useState(() => !!lastEmail());
  const [keep, setKeep] = useState(false);
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const u = await login(email, password, keep);
      rememberEmail(saveEmail ? email.trim().toLowerCase() : "");
      if (window.PasswordCredential && navigator.credentials?.store) {
        try { await navigator.credentials.store(new window.PasswordCredential({ id: email.trim().toLowerCase(), password, name: u.name })); } catch { /* user dismissed */ }
      }
      setPassword("");
      nav(u.must_change_password ? "/primeiro-acesso" : homeFor(u));
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <div className="hidden lg:flex flex-col justify-between bg-slate-950 text-white p-14 relative overflow-hidden">
        <div className="absolute inset-0 hero-grain opacity-90" />
        <img src="/logo-white.png" alt="DPdigi — Seu Departamento Pessoal Digital" data-testid="login-brand-logo" className="relative h-14 w-auto self-start" />
        <div className="relative max-w-lg">
          <h1 className="text-4xl xl:text-5xl font-bold leading-[1.08] tracking-tight" data-testid="login-hero-headline">Seu DP pode ser mais barato, digital e <span className="text-sky-400">muito mais fácil de acompanhar.</span></h1>
          <p className="text-slate-300 mt-5 text-base">Tecnologia, especialistas e inteligência cuidando da rotina — e você entendendo seus números.</p>
          <div className="grid grid-cols-3 gap-4 mt-10">
            {[[Workflow, "Organiza", "Tudo vira solicitação com protocolo e prazo."], [LineChart, "Informa", "Dados trabalhistas viram gestão."], [ShieldCheck, "Protege", "LGPD, auditoria e menor privilégio."]].map(([I, t, d]) => (
              <div key={t} className="border border-white/10 rounded-xl p-4 bg-white/[0.03]"><I className="h-4 w-4 text-indigo-300" /><div className="font-semibold mt-3 text-sm">{t}</div><div className="text-xs text-slate-400 mt-1">{d}</div></div>
            ))}
          </div>
        </div>
        <div className="relative text-xs text-slate-400">DPdigi · Nós operamos e calculamos. Você acompanha tudo em tempo real.</div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm fade-up">
          <img src="/logo.png" alt="DPdigi" className="lg:hidden h-12 w-auto mb-10" />
          <h2 className="text-2xl font-bold tracking-tight">Entrar na plataforma</h2>
          <p className="text-sm text-slate-500 mt-1.5">Acesse com seu e-mail corporativo.</p>
          <form onSubmit={submit} method="post" action="/login" autoComplete="on" className="space-y-4 mt-8" data-testid="login-form">
            <div><Label htmlFor="email">E-mail</Label><Input id="email" name="username" data-testid="login-email-input" type="email" inputMode="email" autoComplete="username" autoCapitalize="none" spellCheck={false} value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-11" required /></div>
            <div><div className="flex items-center justify-between"><Label htmlFor="pw">Senha</Label>
              <Link to={`/esqueci-senha${email ? `?email=${encodeURIComponent(email.trim())}` : ""}`} className="text-xs font-medium text-sky-700 hover:text-sky-900 hover:underline" data-testid="login-forgot-password-link">Esqueci minha senha</Link></div>
              <div className="relative mt-1.5">
                <Input id="pw" name="password" data-testid="login-password-input" type={showPw ? "text" : "password"} autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 pr-11" required />
                <button type="button" data-testid="login-toggle-password" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? "Ocultar senha" : "Mostrar senha"} className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-slate-500 transition-colors hover:text-slate-800">
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2.5">
              <label className="flex items-center gap-2.5 text-sm text-slate-700 cursor-pointer"><Checkbox data-testid="login-remember-email" checked={saveEmail} onCheckedChange={(v) => setSaveEmail(!!v)} />Lembrar meu e-mail</label>
              <label className="flex items-center gap-2.5 text-sm text-slate-700 cursor-pointer"><Checkbox data-testid="login-keep-signed-in" checked={keep} onCheckedChange={(v) => setKeep(!!v)} />Manter conectado neste navegador</label>
              <p className="text-[11px] text-slate-400 leading-snug" data-testid="login-security-note">Por segurança, sua senha não é armazenada pela DPdigi neste navegador. Caso queira salvá-la, utilize o gerenciador de senhas do seu navegador.</p>
            </div>
            <Button data-testid="login-submit-button" type="submit" className="w-full h-11" disabled={busy}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Entrar"}</Button>
          </form>
        </div>
      </div>
    </div>
  );
}
